import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';
  const navBg = scrolled || !isHome ? 'bg-[#F5F1EC]' : 'bg-transparent';
  const textColor = scrolled || !isHome ? 'text-[#3D3A38]' : 'text-[#F5F1EC]';

  const navLinks = [
    { to: '/about', label: 'About Us' },
    { to: '/services', label: 'Services', hasDropdown: true },
    { to: '/projects', label: 'Projects' },
    { to: '/safety', label: 'Safety' },
    { to: '/careers', label: 'Careers' },
    { to: '/contact', label: 'Contact Us' },
  ];

  const serviceLinks = [
    { to: '/services#commercial', label: 'Commercial' },
    { to: '/services#industrial', label: 'Industrial' },
    { to: '/services#residential', label: 'Residential' },
    { to: '/services#panels', label: 'Panel Upgrades' },
    { to: '/services#lighting', label: 'Lighting & Controls' },
    { to: '/services#ev-charging', label: 'EV Charging' },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg} ${scrolled ? 'shadow-sm' : ''}`}
      style={{ backdropFilter: scrolled ? 'none' : 'blur(8px)' }}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_aozat-power/artifacts/19tmgxv4_image.png"
              alt="AOZAT"
              className="h-12 w-auto"
            />
            <span 
              className="text-sm font-medium tracking-[0.15em] uppercase text-[#B8A066]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Electrical
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div key={link.to} className="relative">
                {link.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <Link
                      to={link.to}
                      className={`flex items-center gap-1 text-xs tracking-[0.15em] uppercase transition-colors duration-300 hover:text-[#B8A066] ${textColor}`}
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                      data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.label}
                      <ChevronDown size={12} className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                    </Link>
                    
                    {/* Dropdown */}
                    <div 
                      className={`absolute top-full left-0 pt-4 transition-all duration-300 ${servicesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                    >
                      <div className="bg-[#F5F1EC] border border-[#E5DFD5] shadow-lg min-w-[200px]">
                        {serviceLinks.map((service) => (
                          <Link
                            key={service.to}
                            to={service.to}
                            className="block px-5 py-3 text-xs tracking-[0.1em] uppercase text-[#3D3A38] hover:bg-[#3D3A38] hover:text-[#F5F1EC] transition-colors duration-300"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                            data-testid={`nav-service-${service.label.toLowerCase().replace(' ', '-')}`}
                          >
                            {service.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={link.to}
                    className={`text-xs tracking-[0.15em] uppercase transition-colors duration-300 hover:text-[#B8A066] ${textColor}`}
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 ${textColor}`}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 top-20 bg-[#F5F1EC] transition-all duration-500 ${mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
      >
        <div className="container-custom py-8">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <div key={link.to}>
                <Link
                  to={link.to}
                  className="block py-4 text-sm tracking-[0.15em] uppercase text-[#3D3A38] border-b border-[#E5DFD5]"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
                {link.hasDropdown && (
                  <div className="pl-4 bg-[#EEEBE5]">
                    {serviceLinks.map((service) => (
                      <Link
                        key={service.to}
                        to={service.to}
                        className="block py-3 text-xs tracking-[0.1em] uppercase text-[#5C5855] border-b border-[#E5DFD5]"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};
